import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calendar, MapPin, DollarSign, Mail, Instagram, ArrowRight } from "lucide-react";
import { MARQUEE_WORDS } from "../lib/data";
import { STAFF } from "../lib/staff";

export const Community = () => {
  return (
    <section
      id="community"
      data-testid="community-section"
      className="relative bg-black border-t border-neutral-900 py-24 md:py-32 overflow-hidden"
    >
      {/* Big outlined marquee text */}
      <Marquee gradient={false} speed={45} className="py-4">
        <span className="font-display text-[18vw] md:text-[14vw] leading-none uppercase aa-outline-text whitespace-nowrap px-6">
          {MARQUEE_WORDS.join(" ")}{" "}
        </span>
      </Marquee>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 mt-10 md:mt-16">
        {/* ============= FEATURED EVENT ============= */}
        <div
          id="event"
          data-testid="featured-event"
          className="relative border border-amber-400/30 bg-gradient-to-br from-[#0a0a0a] via-black to-[#0a0a0a] mb-24 md:mb-32"
        >
          {/* corner ornaments */}
          <span className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 border-amber-400" />
          <span className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 border-amber-400" />
          <span className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 border-amber-400" />
          <span className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 border-amber-400" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
            {/* Flyer image */}
            <div className="lg:col-span-5 relative bg-black border-b lg:border-b-0 lg:border-r border-neutral-900 flex items-center justify-center p-6 lg:p-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, ease: "easeOut" }}
                className="relative w-full"
              >
                <img
                  src="/brand/event-flyer.png"
                  alt="Southwest Region Men's and Women's Showcase 2027"
                  className="w-full h-auto object-contain"
                  data-testid="event-flyer-image"
                />
                {/* Badge */}
                <div className="absolute top-2 left-2 bg-amber-400 text-black text-[10px] uppercase tracking-[0.35em] font-bold px-3 py-2 z-10">
                  Inaugural Event
                </div>
              </motion.div>
            </div>

            {/* Story + details */}
            <div className="lg:col-span-7 p-8 md:p-12 lg:p-14">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-5">
                  <span className="w-8 h-px bg-amber-400" />
                  Southwest Region · 2027
                </div>

                <h3
                  data-testid="event-title"
                  className="font-display uppercase text-3xl md:text-5xl lg:text-6xl leading-[0.92] text-white"
                >
                  Men's &amp; Women's
                  <br />
                  <span className="text-amber-400">Showcase.</span>
                </h3>

                <p className="text-[11px] uppercase tracking-[0.35em] text-neutral-400 mt-4">
                  Elite Talent · Global Opportunity
                </p>

                {/* Story */}
                <div
                  data-testid="event-story"
                  className="mt-8 space-y-5 text-neutral-300 font-light leading-relaxed max-w-2xl"
                >
                  <p>
                    This isn't just another tournament — it's the inaugural
                    stage where the next generation of African and American
                    hoopers earn their seat at the table. Two weekends. Two
                    cities. One mission: hand elite talent the global
                    opportunity it has been chasing.
                  </p>
                  <p>
                    Hosted across <span className="text-white">Houston</span>{" "}
                    and <span className="text-white">Bryan College Station, Texas</span>,
                    the Southwest Region Showcase brings men and women from
                    every corner of the diaspora face-to-face with{" "}
                    <span className="text-white">
                      pro international coaches, scouts, and agents
                    </span>
                    , media partners, and the AfroAm family.
                    Skill evaluations in the morning. Conditioning labs in the
                    afternoon. Team showcase games under the lights. Every
                    possession on camera. Every game streamed.
                  </p>
                  <p className="text-neutral-400 italic">
                    "One Game. Two Cultures. One Family." This is where that
                    promise gets played out.
                  </p>
                </div>

                {/* Details grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-neutral-900 border border-neutral-900 mt-10">
                  <div className="bg-black p-5">
                    <div className="flex items-center gap-2 text-amber-400 mb-2">
                      <Calendar size={14} />
                      <span className="text-[10px] uppercase tracking-[0.3em]">
                        Men's Showcase
                      </span>
                    </div>
                    <div className="font-display text-2xl text-white uppercase">
                      Jan 29 – 30, 2027
                    </div>
                  </div>
                  <div className="bg-black p-5">
                    <div className="flex items-center gap-2 text-amber-400 mb-2">
                      <Calendar size={14} />
                      <span className="text-[10px] uppercase tracking-[0.3em]">
                        Women's Showcase
                      </span>
                    </div>
                    <div className="font-display text-2xl text-white uppercase">
                      Feb 26 – 27, 2027
                    </div>
                  </div>
                  <div className="bg-black p-5">
                    <div className="flex items-center gap-2 text-amber-400 mb-2">
                      <MapPin size={14} />
                      <span className="text-[10px] uppercase tracking-[0.3em]">
                        Location
                      </span>
                    </div>
                    <div className="text-white text-sm leading-relaxed">
                      Prairie View A&M · Prairie View, TX
                      <br />
                      Texas A&M · College Station, TX
                    </div>
                  </div>
                  <div className="bg-black p-5">
                    <div className="flex items-center gap-2 text-amber-400 mb-2">
                      <DollarSign size={14} />
                      <span className="text-[10px] uppercase tracking-[0.3em]">
                        Showcase Fee
                      </span>
                    </div>
                    <div className="font-display text-2xl text-white uppercase leading-none">
                      $250 <span className="text-neutral-500 text-base align-middle">/ $300</span>
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.25em] text-neutral-500 mt-2 leading-snug">
                      Early Bird thru Mar 1, 2027
                      <br />
                      Regular after
                    </div>
                  </div>
                </div>

                {/* Featured items */}
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8 text-sm text-neutral-300">
                  {[
                    "Skill & Conditioning Evaluation",
                    "Team Showcase Games",
                    "Media Exposure",
                    "Pro International Coaches & Scouts",
                    "International Agents in Attendance",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className="text-amber-400 mt-1">▸</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {/* CTAs */}
                <div className="flex flex-wrap items-center gap-4 mt-10 pt-8 border-t border-neutral-900">
                  <Link
                    to="/showcase/register"
                    data-testid="event-cta-register"
                    className="inline-flex items-center gap-3 bg-amber-400 text-black px-7 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
                  >
                    <ArrowRight size={14} />
                    Secure Your Spot
                  </Link>
                  <a
                    href="mailto:afroambasketball@gmail.com?subject=Showcase%20Inquiry"
                    data-testid="event-cta-email"
                    className="inline-flex items-center gap-3 border border-white text-white px-7 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
                  >
                    <Mail size={14} />
                    Email Us
                  </a>
                  <a
                    href="https://instagram.com/afroamhoops"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="event-cta-follow"
                    className="inline-flex items-center gap-3 border border-amber-400/40 text-amber-300 px-7 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-400 hover:text-black transition-colors"
                  >
                    <Instagram size={14} />
                    @afroamhoops
                  </a>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                    Limited spots available
                  </span>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* ============= ROSTER / FACES ============= */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-6">
              — The Front Office
            </div>
            <h2
              data-testid="community-title"
              className="font-display uppercase text-4xl md:text-6xl lg:text-7xl leading-[0.9] text-white"
            >
              Faces of the
              <br />
              movement.
            </h2>
          </div>
          <p className="text-neutral-400 max-w-sm font-light leading-relaxed">
            The leaders, scouts, mentors, and operators building AfroAm — on
            both sides of the Atlantic.
          </p>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-900 border border-neutral-900"
          data-testid="staff-grid"
        >
          {STAFF.map((person, i) => (
            <motion.article
              key={person.num}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 3) * 0.08, duration: 0.6 }}
              className="group relative bg-black p-6 md:p-8 hover:bg-[#0a0a0a] transition-colors"
              data-testid={`staff-${person.num}`}
            >
              {/* Photo / initials */}
              <div className="relative aspect-[4/5] overflow-hidden border border-neutral-900 mb-6 bg-gradient-to-br from-neutral-950 to-black">
                {person.image ? (
                  <img
                    src={person.image}
                    alt={person.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-[1.03]"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="font-display text-7xl md:text-8xl text-amber-400/80 leading-none">
                      {person.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.35em] text-neutral-600 mt-4">
                      Portrait coming soon
                    </div>
                  </div>
                )}
                <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm border border-amber-400/40 text-amber-400 text-[10px] uppercase tracking-[0.3em] font-bold px-2 py-1">
                  {person.num}
                </div>
              </div>

              {/* Name + role */}
              <h3 className="font-display uppercase text-2xl md:text-3xl text-white leading-tight">
                {person.name}
              </h3>
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mt-2">
                {person.role}
              </div>

              {/* College + Hometown */}
              {(person.college || person.hometown) && (
                <div className="grid grid-cols-2 gap-3 mt-5 pt-5 border-t border-neutral-900">
                  {person.college && (
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.3em] text-neutral-500">
                        College
                      </div>
                      <div className="text-xs text-white leading-snug mt-1">
                        {person.college}
                      </div>
                    </div>
                  )}
                  {person.hometown && (
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.3em] text-neutral-500">
                        Hometown
                      </div>
                      <div className="text-xs text-white leading-snug mt-1">
                        {person.hometown}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Bio */}
              <p className="text-sm text-neutral-400 font-light leading-relaxed mt-5">
                {person.bio}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
